import { useEffect } from 'react';
import {
  App as CapacitorApp,
  AppState,
  BackButtonListenerEvent,
  RestoredListenerEvent,
  URLOpenListenerEvent,
} from '@capacitor/app';
import { Plugin, PluginListenerHandle } from '@capacitor/core';
import * as Sentry from '@sentry/capacitor';
import { useEventCallback } from 'usehooks-ts';

type CapacitorAppEventMap = {
  appStateChange: AppState;
  pause: void;
  resume: void;
  appUrlOpen: URLOpenListenerEvent;
  appRestoredResult: RestoredListenerEvent;
  backButton: BackButtonListenerEvent;
};

type UseCapacitorListenerOptions = {
  enabled?: boolean;
};

// `addListener` is overloaded per event, so it can't accept a generic event name
const appPlugin = CapacitorApp as unknown as Pick<Plugin, 'addListener'>;

const reportError = (error: unknown): void => {
  Sentry.captureException(error);
};

export const useCapacitorListener = <E extends keyof CapacitorAppEventMap>(
  eventName: E,
  handler: (event: CapacitorAppEventMap[E]) => void,
  options: UseCapacitorListenerOptions = {},
): void => {
  const { enabled = true } = options;
  const stableHandler = useEventCallback(handler);

  useEffect(() => {
    if (!enabled) return undefined;

    let listenerHandle: PluginListenerHandle | undefined;
    let removed = false;

    appPlugin
      .addListener(eventName, stableHandler)
      .then((handle) => {
        // Cleanup ran before registration resolved
        if (removed) {
          handle.remove().catch(reportError);
        } else {
          listenerHandle = handle;
        }
      })
      .catch(reportError);

    return () => {
      removed = true;
      listenerHandle?.remove().catch(reportError);
    };
  }, [eventName, enabled, stableHandler]);
};
